// src/pages/CRM/SFA/ProductFormDialog.js
import React, { useState, useEffect } from 'react';
import {
  Dialog, DialogTitle, DialogContent, DialogActions,
  Button, TextField, Grid, FormControl, InputLabel,
  Select, MenuItem, Divider, InputAdornment, Typography,
  Autocomplete, Box, Paper, Table, TableBody, TableCell,
  TableContainer, TableHead, TableRow, IconButton, Chip,
  FormControlLabel, Checkbox, Alert, LinearProgress, Tooltip
} from '@mui/material';
import {
  Add as AddIcon,
  Delete as DeleteIcon,
  Edit as EditIcon,
  Inventory2 as BundleIcon
} from '@mui/icons-material';
import { useSnackbar } from 'notistack';
import { productService } from './sfaService';
import {
  ProductType, ProductTypeLabels, ProductStatus, ProductStatusLabels, formatCurrency
} from './sfaEnums';

const ProductFormDialog = ({ open, product, categories, canModify, onClose, onSave }) => {
  const { enqueueSnackbar } = useSnackbar();
  const isEdit = Boolean(product);
  const [saving, setSaving] = useState(false);
  const [form, setForm] = useState({
    name: '',
    sku: '',
    product_code: '',
    description: '',
    product_type: ProductType.PRODUCT,
    category_id: '',
    subcategory_id: '',
    product_family: '',
    standard_price: '',
    unit_cost: '',
    currency: 'USD',
    unit_of_measure: 'Each',
    is_recurring: false,
    billing_frequency: '',
    is_taxable: true,
    tax_code: '',
    status: ProductStatus.ACTIVE
  });

  // Bundle state
  const [allProducts, setAllProducts] = useState([]);
  const [bundleItems, setBundleItems] = useState([]);
  const [bundleItemForm, setBundleItemForm] = useState({
    child_product_id: null,
    quantity: 1,
    discount_percent: '',
    override_price: '',
    is_optional: false
  });
  const [selectedBundleProduct, setSelectedBundleProduct] = useState(null);
  const [editingBundleIndex, setEditingBundleIndex] = useState(null);
  const [bundleLoading, setBundleLoading] = useState(false);

  // Fetch all products for bundle selection
  useEffect(() => {
    const fetchProducts = async () => {
      try {
        const response = await productService.getAll({ status: 'ACTIVE', per_page: 500 });
        if (response.success) {
          // Exclude BUNDLE products from being added to bundles (no nested bundles)
          setAllProducts((response.data || []).filter(p => p.product_type !== ProductType.BUNDLE));
        }
      } catch (error) {
        console.error('Error fetching products for bundle:', error);
      }
    };
    if (open) {
      fetchProducts();
    }
  }, [open]);

  useEffect(() => {
    // Reset bundle state
    setBundleItems([]);
    setBundleItemForm({ child_product_id: null, quantity: 1, discount_percent: '', override_price: '', is_optional: false });
    setSelectedBundleProduct(null);
    setEditingBundleIndex(null);

    if (product) {
      setForm({
        name: product.name || '',
        sku: product.sku || '',
        product_code: product.product_code || '',
        description: product.description || '',
        product_type: product.product_type || ProductType.PRODUCT,
        category_id: product.category_id || '',
        subcategory_id: product.subcategory_id || '',
        product_family: product.product_family || '',
        standard_price: product.standard_price || '',
        unit_cost: product.unit_cost || '',
        currency: product.currency || 'USD',
        unit_of_measure: product.unit_of_measure || 'Each',
        is_recurring: product.is_recurring || false,
        billing_frequency: product.billing_frequency || '',
        is_taxable: product.is_taxable !== false,
        tax_code: product.tax_code || '',
        status: product.status || ProductStatus.ACTIVE
      });

      // Load bundle items if editing a bundle product
      if (product.product_type === ProductType.BUNDLE && product.id) {
        loadBundleItems(product.id);
      }
    } else {
      setForm({
        name: '',
        sku: '',
        product_code: '',
        description: '',
        product_type: ProductType.PRODUCT,
        category_id: '',
        subcategory_id: '',
        product_family: '',
        standard_price: '',
        unit_cost: '',
        currency: 'USD',
        unit_of_measure: 'Each',
        is_recurring: false,
        billing_frequency: '',
        is_taxable: true,
        tax_code: '',
        status: ProductStatus.ACTIVE
      });
    }
  }, [product, open]);

  const loadBundleItems = async (productId) => {
    setBundleLoading(true);
    try {
      const response = await productService.getBundleDetails(productId);
      if (response.success && response.data?.bundle_items) {
        setBundleItems(response.data.bundle_items.map(item => ({
          child_product_id: item.child_product_id,
          child_product: item.child_product,
          quantity: item.quantity || 1,
          discount_percent: item.discount_percent || '',
          override_price: item.override_price || '',
          is_optional: item.is_optional || false
        })));
      }
    } catch (error) {
      console.error('Error loading bundle items:', error);
    } finally {
      setBundleLoading(false);
    }
  };

  const handleSave = async () => {
    if (!form.name) {
      enqueueSnackbar('Product name is required', { variant: 'error' });
      return;
    }

    // Validate bundle has at least one item
    if (form.product_type === ProductType.BUNDLE && bundleItems.length === 0) {
      enqueueSnackbar('Bundle must contain at least one product', { variant: 'error' });
      return;
    }

    setSaving(true);
    try {
      const payload = {
        ...form,
        standard_price: form.standard_price ? parseFloat(form.standard_price) : null,
        unit_cost: form.unit_cost ? parseFloat(form.unit_cost) : null,
        category_id: form.category_id || null,
        subcategory_id: form.subcategory_id || null,
        // Include bundle items if this is a bundle product
        bundle_items: form.product_type === ProductType.BUNDLE ? bundleItems.map((item, index) => ({
          child_product_id: item.child_product_id,
          quantity: parseFloat(item.quantity) || 1,
          discount_percent: item.discount_percent ? parseFloat(item.discount_percent) : null,
          override_price: item.override_price ? parseFloat(item.override_price) : null,
          is_optional: item.is_optional || false,
          sort_order: index
        })) : []
      };

      if (isEdit) {
        await productService.update(product.id, payload);
        enqueueSnackbar('Product updated', { variant: 'success' });
      } else {
        await productService.create(payload);
        enqueueSnackbar('Product created', { variant: 'success' });
      }
      onSave();
    } catch (err) {
      enqueueSnackbar(err.response?.data?.message || 'Failed to save product', { variant: 'error' });
    } finally {
      setSaving(false);
    }
  };

  // Bundle item management handlers
  const handleAddBundleItem = () => {
    if (!bundleItemForm.child_product_id) {
      enqueueSnackbar('Please select a product', { variant: 'warning' });
      return;
    }

    // Check if product is already in bundle
    const existingIndex = bundleItems.findIndex(
      item => item.child_product_id === bundleItemForm.child_product_id
    );

    if (editingBundleIndex === null && existingIndex !== -1) {
      enqueueSnackbar('This product is already in the bundle', { variant: 'error' });
      return;
    }

    const newItem = {
      child_product_id: bundleItemForm.child_product_id,
      child_product: selectedBundleProduct,
      quantity: bundleItemForm.quantity || 1,
      discount_percent: bundleItemForm.discount_percent,
      override_price: bundleItemForm.override_price,
      is_optional: bundleItemForm.is_optional
    };

    if (editingBundleIndex !== null) {
      // Update existing item
      setBundleItems(prev => prev.map((item, idx) =>
        idx === editingBundleIndex ? newItem : item
      ));
      setEditingBundleIndex(null);
    } else {
      // Add new item
      setBundleItems(prev => [...prev, newItem]);
    }

    // Reset form
    setBundleItemForm({ child_product_id: null, quantity: 1, discount_percent: '', override_price: '', is_optional: false });
    setSelectedBundleProduct(null);
  };

  const handleEditBundleItem = (index) => {
    const item = bundleItems[index];
    setBundleItemForm({
      child_product_id: item.child_product_id,
      quantity: item.quantity,
      discount_percent: item.discount_percent || '',
      override_price: item.override_price || '',
      is_optional: item.is_optional || false
    });
    setSelectedBundleProduct(item.child_product || allProducts.find(p => p.id === item.child_product_id));
    setEditingBundleIndex(index);
  };

  const handleDeleteBundleItem = (index) => {
    setBundleItems(prev => prev.filter((_, idx) => idx !== index));
    if (editingBundleIndex === index) {
      setEditingBundleIndex(null);
      setBundleItemForm({ child_product_id: null, quantity: 1, discount_percent: '', override_price: '', is_optional: false });
      setSelectedBundleProduct(null);
    }
  };

  const handleCancelBundleEdit = () => {
    setEditingBundleIndex(null);
    setBundleItemForm({ child_product_id: null, quantity: 1, discount_percent: '', override_price: '', is_optional: false });
    setSelectedBundleProduct(null);
  };

  // Calculate bundle total price
  const bundleTotalPrice = bundleItems.reduce((sum, item) => {
    const product = item.child_product || allProducts.find(p => p.id === item.child_product_id);
    if (!product) return sum;

    let itemPrice = item.override_price ? parseFloat(item.override_price) : (product.standard_price || 0);
    if (item.discount_percent) {
      itemPrice = itemPrice * (1 - parseFloat(item.discount_percent) / 100);
    }
    return sum + (itemPrice * (item.quantity || 1));
  }, 0);

  // Filter subcategories based on selected category
  const availableSubcategories = categories.subcategories?.filter(
    sub => sub.parent_category_id === form.category_id
  ) || [];

  return (
    <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth>
      <DialogTitle>{isEdit ? 'Edit Product' : 'New Product'}</DialogTitle>
      <DialogContent>
        <Grid container spacing={2} sx={{ mt: 0.5 }}>
          <Grid item xs={12}>
            <TextField
              fullWidth
              required
              label="Product Name"
              value={form.name}
              onChange={(e) => setForm({ ...form, name: e.target.value })}
              disabled={!canModify}
            />
          </Grid>
          <Grid item xs={12} md={4}>
            <TextField
              fullWidth
              label="SKU"
              value={form.sku}
              onChange={(e) => setForm({ ...form, sku: e.target.value })}
              disabled={!canModify}
            />
          </Grid>
          <Grid item xs={12} md={4}>
            <TextField
              fullWidth
              label="Product Code"
              value={form.product_code}
              onChange={(e) => setForm({ ...form, product_code: e.target.value })}
              disabled={!canModify}
            />
          </Grid>
          <Grid item xs={12} md={4}>
            <FormControl fullWidth>
              <InputLabel>Type</InputLabel>
              <Select
                value={form.product_type}
                label="Type"
                onChange={(e) => setForm({ ...form, product_type: e.target.value })}
                disabled={!canModify}
              >
                {Object.entries(ProductTypeLabels).map(([key, label]) => (
                  <MenuItem key={key} value={key}>{label}</MenuItem>
                ))}
              </Select>
            </FormControl>
          </Grid>
          <Grid item xs={12}>
            <TextField
              fullWidth
              multiline
              rows={2}
              label="Description"
              value={form.description}
              onChange={(e) => setForm({ ...form, description: e.target.value })}
              disabled={!canModify}
            />
          </Grid>

          {/* Category Dropdown */}
          <Grid item xs={12} md={4}>
            <FormControl fullWidth>
              <InputLabel>Category</InputLabel>
              <Select
                value={form.category_id}
                label="Category"
                onChange={(e) => setForm({ ...form, category_id: e.target.value, subcategory_id: '' })}
                disabled={!canModify}
              >
                <MenuItem value="">None</MenuItem>
                {categories.categories?.map((cat) => (
                  <MenuItem key={cat.id} value={cat.id}>
                    {cat.name} ({cat.product_count})
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Grid>

          {/* Subcategory Dropdown */}
          <Grid item xs={12} md={4}>
            <FormControl fullWidth disabled={!form.category_id}>
              <InputLabel>Subcategory</InputLabel>
              <Select
                value={form.subcategory_id}
                label="Subcategory"
                onChange={(e) => setForm({ ...form, subcategory_id: e.target.value })}
                disabled={!canModify || !form.category_id}
              >
                <MenuItem value="">None</MenuItem>
                {availableSubcategories.map((sub) => (
                  <MenuItem key={sub.id} value={sub.id}>
                    {sub.name} ({sub.product_count})
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
            {!form.category_id && (
              <Typography variant="caption" color="text.secondary">
                Select a category first
              </Typography>
            )}
          </Grid>

          <Grid item xs={12} md={4}>
            <Autocomplete
              freeSolo
              options={categories.product_families?.map(f => f.name) || []}
              value={form.product_family}
              onChange={(e, value) => setForm({ ...form, product_family: value || '' })}
              onInputChange={(e, value) => setForm({ ...form, product_family: value })}
              disabled={!canModify}
              renderInput={(params) => (
                <TextField {...params} label="Product Family" placeholder="Type or select..." />
              )}
            />
          </Grid>

          <Grid item xs={12}>
            <Divider sx={{ my: 1 }}>Pricing</Divider>
          </Grid>
          <Grid item xs={12} md={4}>
            <TextField
              fullWidth
              label="Standard Price"
              type="number"
              value={form.standard_price}
              onChange={(e) => setForm({ ...form, standard_price: e.target.value })}
              InputProps={{
                startAdornment: <InputAdornment position="start">$</InputAdornment>
              }}
              disabled={!canModify}
            />
          </Grid>
          <Grid item xs={12} md={4}>
            <TextField
              fullWidth
              label="Unit Cost"
              type="number"
              value={form.unit_cost}
              onChange={(e) => setForm({ ...form, unit_cost: e.target.value })}
              InputProps={{
                startAdornment: <InputAdornment position="start">$</InputAdornment>
              }}
              disabled={!canModify}
            />
          </Grid>
          <Grid item xs={12} md={4}>
            <TextField
              fullWidth
              label="Unit of Measure"
              value={form.unit_of_measure}
              onChange={(e) => setForm({ ...form, unit_of_measure: e.target.value })}
              disabled={!canModify}
            />
          </Grid>
          {isEdit && (
            <Grid item xs={12} md={4}>
              <FormControl fullWidth>
                <InputLabel>Status</InputLabel>
                <Select
                  value={form.status}
                  label="Status"
                  onChange={(e) => setForm({ ...form, status: e.target.value })}
                  disabled={!canModify}
                >
                  {Object.entries(ProductStatusLabels).map(([key, label]) => (
                    <MenuItem key={key} value={key}>{label}</MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>
          )}

          {/* Bundle Configuration Section */}
          {form.product_type === ProductType.BUNDLE && (
            <Grid item xs={12}>
              <Divider sx={{ my: 2 }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  <BundleIcon /> Bundle Components
                </Box>
              </Divider>

              <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                Add products to include in this bundle. You can set quantity, apply discounts, or override prices for each component.
              </Typography>

              {bundleLoading ? (
                <LinearProgress sx={{ my: 2 }} />
              ) : (
                <>
                  {/* Bundle item form */}
                  <Paper variant="outlined" sx={{ p: 2, mb: 2, bgcolor: 'grey.50' }}>
                    <Grid container spacing={2} alignItems="flex-end">
                      <Grid item xs={12} sm={4}>
                        <Autocomplete
                          size="small"
                          options={allProducts.filter(p =>
                            // Exclude current product and already added products (unless editing)
                            p.id !== product?.id &&
                            (editingBundleIndex !== null || !bundleItems.some(item => item.child_product_id === p.id))
                          )}
                          getOptionLabel={(option) => `${option.name} (${option.sku || 'No SKU'})`}
                          value={selectedBundleProduct}
                          onChange={(e, newValue) => {
                            setSelectedBundleProduct(newValue);
                            setBundleItemForm(prev => ({
                              ...prev,
                              child_product_id: newValue?.id || null
                            }));
                          }}
                          disabled={!canModify}
                          renderInput={(params) => (
                            <TextField {...params} label="Product" placeholder="Search products..." />
                          )}
                          renderOption={(props, option) => (
                            <li {...props}>
                              <Box>
                                <Typography variant="body2">{option.name}</Typography>
                                <Typography variant="caption" color="text.secondary">
                                  {option.sku || 'No SKU'} • {formatCurrency(option.standard_price || 0)}
                                </Typography>
                              </Box>
                            </li>
                          )}
                        />
                      </Grid>
                      <Grid item xs={6} sm={1.5}>
                        <TextField
                          fullWidth
                          size="small"
                          label="Qty"
                          type="number"
                          value={bundleItemForm.quantity}
                          onChange={(e) => setBundleItemForm(prev => ({ ...prev, quantity: e.target.value }))}
                          inputProps={{ min: 1, step: 0.01 }}
                          disabled={!canModify}
                        />
                      </Grid>
                      <Grid item xs={6} sm={2}>
                        <TextField
                          fullWidth
                          size="small"
                          label="Discount %"
                          type="number"
                          value={bundleItemForm.discount_percent}
                          onChange={(e) => setBundleItemForm(prev => ({ ...prev, discount_percent: e.target.value }))}
                          InputProps={{
                            endAdornment: <InputAdornment position="end">%</InputAdornment>
                          }}
                          disabled={!canModify}
                        />
                      </Grid>
                      <Grid item xs={6} sm={2}>
                        <TextField
                          fullWidth
                          size="small"
                          label="Override Price"
                          type="number"
                          value={bundleItemForm.override_price}
                          onChange={(e) => setBundleItemForm(prev => ({ ...prev, override_price: e.target.value }))}
                          InputProps={{
                            startAdornment: <InputAdornment position="start">$</InputAdornment>
                          }}
                          disabled={!canModify}
                        />
                      </Grid>
                      <Grid item xs={6} sm={1.5}>
                        <FormControlLabel
                          control={
                            <Checkbox
                              size="small"
                              checked={bundleItemForm.is_optional}
                              onChange={(e) => setBundleItemForm(prev => ({ ...prev, is_optional: e.target.checked }))}
                              disabled={!canModify}
                            />
                          }
                          label={<Typography variant="body2">Optional</Typography>}
                        />
                      </Grid>
                      <Grid item xs={12} sm={1}>
                        <Box sx={{ display: 'flex', gap: 0.5 }}>
                          <Tooltip title={editingBundleIndex !== null ? 'Update' : 'Add'}>
                            <IconButton
                              size="small"
                              color="primary"
                              onClick={handleAddBundleItem}
                              disabled={!canModify || !bundleItemForm.child_product_id}
                            >
                              {editingBundleIndex !== null ? <EditIcon /> : <AddIcon />}
                            </IconButton>
                          </Tooltip>
                          {editingBundleIndex !== null && (
                            <Tooltip title="Cancel">
                              <IconButton size="small" onClick={handleCancelBundleEdit}>
                                <DeleteIcon />
                              </IconButton>
                            </Tooltip>
                          )}
                        </Box>
                      </Grid>
                    </Grid>
                  </Paper>

                  {/* Bundle items table */}
                  {bundleItems.length > 0 ? (
                    <TableContainer component={Paper} variant="outlined">
                      <Table size="small">
                        <TableHead>
                          <TableRow>
                            <TableCell>Product</TableCell>
                            <TableCell align="center">Qty</TableCell>
                            <TableCell align="right">Unit Price</TableCell>
                            <TableCell align="right">Discount</TableCell>
                            <TableCell align="right">Line Total</TableCell>
                            <TableCell align="center">Optional</TableCell>
                            {canModify && <TableCell align="center">Actions</TableCell>}
                          </TableRow>
                        </TableHead>
                        <TableBody>
                          {bundleItems.map((item, index) => {
                            const itemProduct = item.child_product || allProducts.find(p => p.id === item.child_product_id);
                            const unitPrice = item.override_price ? parseFloat(item.override_price) : (itemProduct?.standard_price || 0);
                            const discountedPrice = item.discount_percent
                              ? unitPrice * (1 - parseFloat(item.discount_percent) / 100)
                              : unitPrice;
                            const lineTotal = discountedPrice * (item.quantity || 1);

                            return (
                              <TableRow key={index} sx={{ bgcolor: editingBundleIndex === index ? 'action.selected' : 'inherit' }}>
                                <TableCell>
                                  <Box>
                                    <Typography variant="body2" fontWeight={500}>
                                      {itemProduct?.name || 'Unknown Product'}
                                    </Typography>
                                    <Typography variant="caption" color="text.secondary">
                                      {itemProduct?.sku || 'No SKU'}
                                    </Typography>
                                  </Box>
                                </TableCell>
                                <TableCell align="center">{item.quantity}</TableCell>
                                <TableCell align="right">
                                  {item.override_price ? (
                                    <Tooltip title="Custom price">
                                      <Typography variant="body2" color="info.main">
                                        {formatCurrency(parseFloat(item.override_price))}
                                      </Typography>
                                    </Tooltip>
                                  ) : (
                                    formatCurrency(unitPrice)
                                  )}
                                </TableCell>
                                <TableCell align="right">
                                  {item.discount_percent ? (
                                    <Chip size="small" label={`${item.discount_percent}%`} color="warning" />
                                  ) : '-'}
                                </TableCell>
                                <TableCell align="right">
                                  <Typography fontWeight={600}>{formatCurrency(lineTotal)}</Typography>
                                </TableCell>
                                <TableCell align="center">
                                  {item.is_optional ? (
                                    <Chip size="small" label="Optional" variant="outlined" />
                                  ) : (
                                    <Chip size="small" label="Required" color="primary" variant="outlined" />
                                  )}
                                </TableCell>
                                {canModify && (
                                  <TableCell align="center">
                                    <IconButton size="small" onClick={() => handleEditBundleItem(index)}>
                                      <EditIcon fontSize="small" />
                                    </IconButton>
                                    <IconButton size="small" color="error" onClick={() => handleDeleteBundleItem(index)}>
                                      <DeleteIcon fontSize="small" />
                                    </IconButton>
                                  </TableCell>
                                )}
                              </TableRow>
                            );
                          })}
                          <TableRow sx={{ bgcolor: 'grey.100' }}>
                            <TableCell colSpan={4} align="right">
                              <Typography variant="subtitle2">Bundle Total (Required Items):</Typography>
                            </TableCell>
                            <TableCell align="right">
                              <Typography variant="h6" color="primary">{formatCurrency(bundleTotalPrice)}</Typography>
                            </TableCell>
                            <TableCell colSpan={2} />
                          </TableRow>
                        </TableBody>
                      </Table>
                    </TableContainer>
                  ) : (
                    <Alert severity="info">
                      No products added to this bundle yet. Use the form above to add products.
                    </Alert>
                  )}
                </>
              )}
            </Grid>
          )}
        </Grid>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>Cancel</Button>
        {canModify && (
          <Button variant="contained" onClick={handleSave} disabled={saving}>
            {saving ? 'Saving...' : isEdit ? 'Update' : 'Create'}
          </Button>
        )}
      </DialogActions>
    </Dialog>
  );
};

export default ProductFormDialog;